import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Send, Timer, X } from 'lucide-react';
import { LIMITS, type Business, type Currency, type PublicQuote, type Quote, type MiniSiteGallery } from '@worki/shared';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { currentLocale } from '../i18n.js';
import { formatDate, formatDuration, money } from '../lib/format.js';
import { QuoteAlbums } from './QuoteAlbums.js';
import { QuoteTotalsPanel } from './QuoteTotals.js';

/**
 * The quote as the customer receives it — masthead, "to", line items, totals
 * and the footer line.
 *
 * Every copy of a quote comes through here: the list's preview, the detail
 * screen, the editor's live preview and — since 2.11 — the public link the
 * customer opens. It reads nothing from the session, so the customer's page
 * can render it without one; the business is on the document, put there by
 * `quoteToDoc` for the owner and by `publicQuoteToDoc` for the customer.
 *
 * That is the whole point of it. The public page used to carry a second
 * layout of its own, and the two drifted: the owner's copy grew a cover band,
 * a round logo, the business phone, a labelled subject block and the
 * business's own sign-off, none of which the customer ever saw. Whatever is
 * checked before sending is now literally the thing that was checked.
 */
export function QuoteDocument({ doc }: { doc: QuoteDoc }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const business = doc.business;
  const amount = (value: number) => money(value, doc.currency, locale);
  // 1.62 — the quantity reads like the amounts beside it: "1,250", not "1250".
  const quantity = new Intl.NumberFormat(locale);
  const items = doc.items.filter((item) => item.description.trim());

  return (
    <div className="p-6 space-y-5">
      {/*
        QUO-024 — the masthead: the business's cover with its logo sitting round
        on top of it, the same pairing the mini-site opens with. A customer who
        has seen one recognises the other, and a business with neither still
        gets a header — the band falls back to the accent and the circle to the
        initial, so the document never opens on a blank rectangle.
      */}
      <div className="-mx-6 -mt-6">
        <div className="relative h-36 sm:h-44 overflow-hidden bg-gradient-to-bl from-primary to-primary/70">
          {business?.coverUrl && (
            /*
              Not `loading="lazy"`. These two are the first thing the document
              shows, and inside the preview — which mounts on top of the editor
              rather than being scrolled to — the browser never decided they had
              come into view: the band stayed on its fallback colour and the
              logo on its initial, on a business that had uploaded both.
            */
            <img src={business.coverUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
          )}
          {/*
            The logo hangs off the band's own lower edge, and it is the only
            thing that does. The identity line used to be dragged up with it —
            the whole row carried `-mt-9` — which laid the business name and
            phone across the bottom of the cover photo: near-black text on
            whatever the business had uploaded, measured at the full height of
            the name. The row sits below the band now and the photo is left to
            the logo.
          */}
          <div className="absolute bottom-4 start-6 z-10">
            {business?.logoUrl ? (
              <img
                src={business.logoUrl}
                alt={business.name}
                className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover bg-card ring-4 ring-card"
              />
            ) : (
              <span className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-card text-foreground text-2xl font-bold flex items-center justify-center ring-4 ring-card">
                {business?.name?.slice(0, 1) ?? '—'}
              </span>
            )}
          </div>
        </div>
        <div className="pt-3 pb-4 px-6 min-h-[3rem] border-b border-border">
          {/* The slogan rides on the same line as the name and is allowed to
              be cut before it: whose quote this is matters, what they say
              about themselves is the part a narrow screen can lose. */}
          <p className="text-lg font-bold truncate">
            {business?.name ?? '—'}
            {business?.slogan && (
              <>
                <span className="text-muted-foreground font-normal"> | </span>
                <span className="text-sm font-medium text-muted-foreground">{business.slogan}</span>
              </>
            )}
          </p>
          {business?.phone && (
            <p className="text-xs text-muted-foreground" dir="ltr">
              {business.phone}
            </p>
          )}
          {/* 2.5 — the ח.פ / ע.מ, never without the words that say what it is:
              nine bare digits under a phone number read as a second phone
              number. */}
          {business?.taxId && (
            <p className="text-xs text-muted-foreground">
              {t('profile.taxId')}: <span className="ltr-nums">{business.taxId}</span>
            </p>
          )}
          <p className="text-sm font-semibold text-primary">
            {doc.number === null ? t('quotes.draftDocument') : t('quotes.numberShort', { number: doc.number })}
          </p>
        </div>
      </div>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground">{t('quotes.toHonour')}</p>
          <p className="text-sm font-semibold">{doc.customerName || '—'}</p>
          <div className="mt-1 space-y-0.5">
            {doc.customerPhone && (
              <p className="text-xs text-muted-foreground" dir="ltr">
                {doc.customerPhone}
              </p>
            )}
            {doc.customerEmail && (
              <p className="text-xs text-muted-foreground truncate" dir="ltr">
                {doc.customerEmail}
              </p>
            )}
            {doc.customerCity && <p className="text-xs text-muted-foreground">{t('quotes.projectLocation')}: {doc.customerCity}</p>}
          </div>
        </div>
        <div className="text-end shrink-0">
          {doc.date && (
            <p className="text-xs text-muted-foreground">
              {t('quotes.docDate', { date: formatDate(doc.date, locale) })}
            </p>
          )}
          {/* 1.20 — how long the quote is good for, in days: a date under the
              quote's own read as a second date. Nothing once it is approved. */}
          {doc.validDays != null && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {t('quotes.validFor', { count: doc.validDays })}
            </p>
          )}
        </div>
      </div>

      {doc.title && (
        <div className="rounded-xl bg-muted/60 p-3">
          <p className="text-[11px] text-muted-foreground mb-1">{t('quotes.serviceDescription')}</p>
          <p className="text-sm whitespace-pre-wrap">{doc.title}</p>
        </div>
      )}

      {/*
        The money columns are sized by what is in them, not by a share of the
        row. Five equal columns gave each price a fifth of a phone's width —
        about 58px — and `₪ 3,600.00` does not fit in 58px: the unit price and
        the line total were both cut off on every quote with a four-figure line
        in it. The description is the one that can afford to be shortened, so it
        is the one that takes what is left.
      */}
      <div className="border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead className="bg-muted text-[11px] font-semibold text-muted-foreground"><tr>
            <th className="text-start px-2 py-2">{t('quotes.description')}</th>
            <th className="text-center px-2 py-2">{t('quotes.quantity')}</th>
            <th className="text-center px-2 py-2">{t('quotes.unitPrice')}</th>
            <th className="text-end px-2 py-2">{t('quotes.lineTotal')}</th>
          </tr></thead>
          <tbody>{items.map((item, index) => <tr key={item.id ?? index} className="border-t border-border">
            <td className="px-2 py-2 break-words min-w-16">{item.description}</td>
            <td className="px-2 py-2 text-center ltr-nums">{quantity.format(item.quantity)}</td>
            {/* 04.10 — a line at no price is a work inside a priced job
                ("שיפוץ: ריצוף, פרגולה, דק" said with one total); ₪0.00 beside
                it reads as free, so it says "included". */}
            <td className="px-2 py-2 text-center ltr-nums whitespace-nowrap">{item.unitPrice === 0 ? t('quotes.included') : amount(item.unitPrice)}</td>
            <td className="px-2 py-2 text-end font-medium ltr-nums whitespace-nowrap">{item.unitPrice === 0 ? t('quotes.included') : amount(item.quantity * item.unitPrice)}</td>
          </tr>)}</tbody>
        </table>
        {items.length === 0 && <p className="px-3 py-3 text-xs text-muted-foreground">{t('quotes.noItemsYet')}</p>}
      </div>

      {doc.notes && (
        <div className="bg-muted/50 rounded-xl p-3">
          <p className="text-[11px] text-muted-foreground mb-1">{t('quotes.notes')}</p>
          <p className="text-sm whitespace-pre-wrap">{doc.notes}</p>
        </div>
      )}
      {doc.legalText && (
        <div className="bg-muted/50 rounded-xl p-3">
          <p className="text-[11px] text-muted-foreground mb-1">{t('quotes.notesTerms')}</p>
          <p className="text-xs text-muted-foreground whitespace-pre-wrap">{doc.legalText}</p>
        </div>
      )}

      {/*
        The totals come from the one panel rather than from a second copy of
        the same rows written here. The copy that used to live in this file
        printed no "before VAT" line and never said when prices already
        include it — two rows the customer's page was showing and the owner's
        preview was not, on the same quote.
      */}
      <QuoteTotalsPanel
        totals={doc.totals}
        currency={doc.currency}
        discountLabel={doc.discountLabel}
        pricesIncludeVat={doc.pricesIncludeVat}
      />

      {/* QUO-010 — how long the customer actually spent with the document. */}
      {doc.viewedSeconds !== undefined && doc.sent && (
        <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
          <Timer className="h-3.5 w-3.5" />
          <span className="ltr-nums">
            {doc.viewedSeconds > 0 ? formatDuration(doc.viewedSeconds) : t('quotes.notViewedYet')}
          </span>
        </div>
      )}

      {/* Two lines, not one or the other — which is what the PDF has always
          printed. A business's own sign-off ("thank you for choosing us") is
          not a substitute for saying where the document came from, and the
          preview claiming otherwise meant the screen and the file a customer
          receives disagreed about the foot of the same quote. */}
      <div className="text-center pt-1 space-y-0.5">
        {business?.quoteFooter && (
          <p className="text-[11px] text-muted-foreground">{business.quoteFooter}</p>
        )}
        <p className="text-[10px] text-muted-foreground/70">{t('quotes.producedBy')}</p>
      </div>
      <QuoteAlbums galleries={doc.galleries ?? []} businessName={doc.business?.name ?? ''} />
    </div>
  );
}

/**
 * The business as the document prints it.
 *
 * Narrower than `Business` on purpose: it is the intersection of what the
 * owner's session holds and what the customer's payload carries, so the same
 * masthead can be built from either and neither side can quietly start
 * printing something the other has no way to show.
 */
export interface QuoteDocBusiness {
  name: string;
  slogan: string | null;
  phone: string | null;
  taxId: string | null;
  logoUrl: string | null;
  coverUrl: string | null;
  quoteFooter: string | null;
}

/** What the document needs, from either a saved quote or a form in progress. */
export interface QuoteDoc {
  business: QuoteDocBusiness | null;
  /** Null while the quote is still a draft with no issued number. */
  number: number | null;
  currency: Currency;
  customerName: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  customerCity?: string | null;
  /** Null on a quote that has neither been sent nor saved yet. */
  date: string | null;
  /** How many days the quote is valid for. Null once the customer has approved it. */
  validDays?: number | null;
  title: string;
  notes?: string | null;
  legalText?: string | null;
  discountLabel?: string | null;
  pricesIncludeVat?: boolean;
  items: Array<{ id?: string; description: string; quantity: number; unitPrice: number }>;
  galleries?: Array<Pick<MiniSiteGallery, 'id' | 'name' | 'description' | 'images'>>;
  totals: Quote['totals'];
  sent?: boolean;
  /** Owner-only: how long the customer has spent with the document. */
  viewedSeconds?: number;
}

/** The masthead's fields, off the signed-in business. */
export function businessToDocBusiness(business: Business | null | undefined): QuoteDocBusiness | null {
  if (!business) return null;
  return {
    name: business.name,
    slogan: business.slogan,
    phone: business.phone,
    taxId: business.taxId,
    logoUrl: business.logoUrl,
    coverUrl: business.coverUrl,
    quoteFooter: business.quoteFooter,
  };
}

/**
 * The owner's copy of a saved quote. A quote that never chose its days is valid
 * for the platform's, which the screen passes in from the session.
 */
export function quoteToDoc(
  quote: Quote,
  business: Business | null | undefined,
  defaultValidDays: number = LIMITS.quoteLinkTtlDays,
): QuoteDoc {
  return {
    business: businessToDocBusiness(business),
    number: quote.number,
    currency: quote.currency,
    customerName: quote.customer?.names?.join(' · ') || quote.customer?.name || '',
    customerPhone: quote.customer?.phones?.join(' · ') || quote.customer?.phone || null,
    customerEmail: quote.customer?.emails?.join(' · ') || quote.customer?.email || null,
    customerCity: quote.customer?.city ?? null,
    date: quote.sentAt ?? quote.createdAt,
    validDays: quote.status === 'approved' ? null : (quote.validDays ?? defaultValidDays),
    title: quote.title,
    notes: quote.notes,
    legalText: quote.legalText,
    discountLabel: quote.discountLabel,
    pricesIncludeVat: quote.pricesIncludeVat,
    items: quote.items,
    galleries: quote.galleries,
    totals: quote.totals,
    sent: Boolean(quote.sentAt),
    viewedSeconds: quote.totalViewSeconds,
  };
}

/**
 * The customer's copy, off the public payload.
 *
 * The one asymmetry is deliberate: no `viewedSeconds`. How long somebody has
 * been reading their own quote is the owner's tracking, and the document does
 * not carry it to the person being tracked.
 */
export function publicQuoteToDoc(quote: PublicQuote): QuoteDoc {
  return {
    business: {
      name: quote.business.name,
      slogan: quote.business.slogan,
      phone: quote.business.phone,
      taxId: quote.business.taxId,
      logoUrl: quote.business.logoUrl,
      coverUrl: quote.business.coverUrl,
      quoteFooter: quote.business.quoteFooter,
    },
    number: quote.number,
    currency: quote.currency,
    customerName: quote.customerName,
    customerPhone: quote.customerPhone,
    customerEmail: quote.customerEmail,
    customerCity: quote.customerCity,
    date: quote.issuedAt,
    validDays: quote.status === 'approved' ? null : quote.validDays,
    title: quote.title,
    notes: quote.notes,
    legalText: quote.legalText,
    discountLabel: quote.discountLabel,
    pricesIncludeVat: quote.pricesIncludeVat,
    items: quote.items,
    galleries: quote.galleries,
    totals: quote.totals,
    sent: true,
  };
}

/**
 * The preview, as its own overlay rather than through `Modal`: the document is
 * the whole content and carries its own padding, so the shared dialog's header
 * and body padding would only box it in.
 */
export function QuoteDocumentDialog({
  quote,
  onClose,
  onSend,
}: {
  quote: Quote | null;
  onClose: () => void;
  onSend?: (quote: Quote) => void;
}) {
  const { t } = useTranslation();
  const { session } = useSession();

  /*
   * The list endpoint returns quotes without their line items — 25 rows of
   * items is a lot of payload for a screen that shows only the total — so the
   * preview opened on an empty items table. Fetching the one quote fills it,
   * and the key is the detail screen's own, so arriving from there costs
   * nothing.
   */
  const full = useQuery({
    queryKey: ['quote', quote?.id],
    queryFn: () => api.get<Quote>(`/quotes/${quote?.id}`),
    enabled: Boolean(quote),
  });

  if (!quote) return null;
  const document_ = full.data ?? quote;
  const sendable = !document_.isLocked && document_.status !== 'approved' && document_.status !== 'rejected';

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 bg-foreground/25 animate-in fade-in duration-150"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="bg-card rounded-2xl shadow-2xl w-full max-w-[760px] max-h-[calc(92vh-4rem)] md:max-h-[92vh] mb-16 md:mb-0 overflow-y-auto animate-fade-up"
        role="dialog"
        aria-modal="true"
        aria-label={t('quotes.viewDocument')}
      >
        <div className="flex items-center justify-between gap-2 p-2 pb-0">
          {/* 1.21 / 1.43 — a tap on a quote in the list opens this popup, so the
              page that holds what the popup leaves out (the send history, the
              reminder, the statistics) is one tap from it. At the top rather
              than in the footer, which a phone's width is already full of. */}
          <Link
            to={`/quotes/${document_.id}`}
            className="px-2 py-1.5 rounded-lg text-xs font-medium text-primary hover:bg-muted transition-colors"
          >
            {t('quotes.fullDetails')}
          </Link>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <QuoteDocument doc={quoteToDoc(document_, session?.business, session?.features.quoteLinkTtlDays)} />
        <div className="p-4 border-t border-border flex justify-center gap-2 sticky bottom-0 bg-card">
          {onSend && sendable && (
            <button
              type="button"
              onClick={() => onSend(document_)}
              className="h-10 px-4 rounded-lg bg-info/10 text-info text-sm font-medium hover:bg-info/20 flex items-center gap-1.5 transition-colors"
            >
              <Send className="h-3.5 w-3.5" />
              {document_.sentAt ? t('quotes.sendAgain') : t('quotes.sendQuote')}
            </button>
          )}
          <button
            type="button"
            onClick={() => void api.download(`/quotes/${document_.id}/pdf`, `quote-${document_.id}.pdf`)}
            className="h-10 px-4 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors flex items-center"
          >
            {t('quotes.downloadPdf')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-6 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
